import { InfoTextQuestion } from '@components/form/questions/info-text-question';
import { Card } from 'antd';
import { Maybe } from 'graphql/jsutils/Maybe';
import React, { FC, useCallback } from 'react';
import { QuestionProps } from '../../../pages/form';
import { Form_FormValueType, Input_Form_FormValueMsg } from '../../../src/generated/graphql';
import { AddressQuestion } from './address-question';
import { BoolQuestion } from './bool-question';
import { DocumentQuestion } from './document-question';
import { DoubleQuestion } from './double-question';
import { EmailQuestion } from './email-question';
import { IntQuestion } from './int-question';
import { KnowledgeQuestion } from './knowledge-question';
import { LocalDateQuestion } from './local-date-question';
import { LocalDateTimeQuestion } from './local-date-time-question';
import { LocalTimeQuestion } from './local-time-question';
import { MultiKnowledgeQuestion } from './multi-knowledge-question';
import { MultiSelectQuestion } from './multi-select-question';
import { QuestionNotFound } from './question-not-found';
import { SelectQuestion } from './select-question';
import { StringQuestion } from './string-question';

interface QuestionFactoryProps extends Omit<QuestionProps, 'onAnswer'> {
  clinicId: string;
  formRevisionGroupId: string;
  answerFormQuestion: (
    newValue: Input_Form_FormValueMsg | undefined,
    doAutoMoveToNext: boolean,
    sequenceAnswerId: string,
  ) => void;
}

const doAutoMoveToNextTypes: Form_FormValueType[] = [
  Form_FormValueType.FormValueTypeBoolean,
  Form_FormValueType.FormValueTypeSelect,
];

export const QuestionFactory: FC<QuestionFactoryProps> = ({
  clinicId,
  formRevisionGroupId,
  answerFormQuestion,
  ...questionProps
}) => {
  const question = questionProps?.question;
  const answer = questionProps?.answer;
  const type = question === null ? Form_FormValueType.FormValueTypeNone : question?.type;

  // const doAutoMoveToNext = type && doAutoMoveToNextTypes.includes(type);
  const doAutoMoveToNext = false;

  let questionRender: React.ReactNode = null;

  const handleAnswer = useCallback(
    (value: Maybe<Input_Form_FormValueMsg> | undefined) => {
      if (!answer?.id) {
        return;
      }
      answerFormQuestion(value ?? undefined, doAutoMoveToNext, answer.id);
    },
    [answerFormQuestion, doAutoMoveToNext, answer],
  );

  const extendedQuestionProps: QuestionProps = {
    ...questionProps,
    onAnswer: handleAnswer,
  };

  switch (type) {
    case Form_FormValueType.FormValueTypeAddress:
      questionRender = <AddressQuestion {...extendedQuestionProps} />;
      break;
    case Form_FormValueType.FormValueTypeEmail:
      questionRender = <EmailQuestion {...extendedQuestionProps} />;
      break;
    case Form_FormValueType.FormValueTypeDouble:
      questionRender = <DoubleQuestion {...extendedQuestionProps} />;
      break;
    case Form_FormValueType.FormValueTypeBoolean:
      questionRender = <BoolQuestion {...extendedQuestionProps} />;
      break;
    case Form_FormValueType.FormValueTypeString:
      questionRender = <StringQuestion {...extendedQuestionProps} />;
      break;
    case Form_FormValueType.FormValueTypeInteger:
      questionRender = <IntQuestion {...extendedQuestionProps} clinicId={clinicId} />;
      break;
    case Form_FormValueType.FormValueTypeMultiSelect:
      questionRender = <MultiSelectQuestion {...extendedQuestionProps} clinicId={clinicId} />;
      break;
    case Form_FormValueType.FormValueTypeSelect:
      questionRender = <SelectQuestion {...extendedQuestionProps} clinicId={clinicId} />;
      break;
    case Form_FormValueType.FormValueTypeLocalDate:
      questionRender = <LocalDateQuestion {...extendedQuestionProps} />;
      break;
    case Form_FormValueType.FormValueTypeLocalTime:
      questionRender = <LocalTimeQuestion {...extendedQuestionProps} />;
      break;
    case Form_FormValueType.FormValueTypeLocalDateTime:
      questionRender = <LocalDateTimeQuestion {...extendedQuestionProps} />;
      break;
    case Form_FormValueType.FormValueTypeDocument:
      questionRender = (
        <DocumentQuestion
          {...extendedQuestionProps}
          clinicId={clinicId}
          formRevisionGroupId={formRevisionGroupId}
        />
      );
      break;
    case Form_FormValueType.FormValueTypeKnowledge:
      questionRender = <KnowledgeQuestion />;
      break;
    case Form_FormValueType.FormValueTypeMultiKnowledge:
      questionRender = <MultiKnowledgeQuestion />;
      break;
    case Form_FormValueType.FormValueTypeInfoText:
      questionRender = <InfoTextQuestion {...extendedQuestionProps} />;
      break;
    case Form_FormValueType.FormValueTypeNone:
      break;
    default:
      questionRender = <QuestionNotFound />;
  }

  return questionRender ? <Card>{questionRender}</Card> : null;
};
